import moment from 'moment'
import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useParams, useSearchParams } from 'react-router-dom'
import { useBreadcrumbSelection } from 'components/breadcrumb-bar/use-breadcrumb-selection'
import {
	AnalyticsDimensionId,
	AnalyticsMetricId,
	AnalyticsReport,
	AnalyticsScopeBody,
	ParentFilter
} from 'services/user'
import { CHART_MAX_CATEGORIES } from 'api-models'
import { DIMENSIONS, DIMENSION_CATEGORIES } from './dimension-registry'
import type { DimensionId } from '../types'

// The live-data layer for the Analytics Report page (DEV-2377). Every hook keys
// its react-query cache on (config slice + scope + date range), so changing a
// metric/dimension refetches only that section, and changing scope/date refetches
// all sections with the same config. Replaces the prototype's mock seams
// (mock-data / preview-series / key-metrics-data).

interface AnalyticsScope {
	accountId?: string
	assetId?: string
	body: AnalyticsScopeBody
	/** Stable cache-key fragment for every analytics query. */
	key: (string | undefined)[]
	ready: boolean
}

// Scope (website via route + ad-accounts/campaigns via breadcrumb) + date window.
export const useAnalyticsScope = (): AnalyticsScope => {
	const { accountId, assetId } = useParams<{ accountId: string; assetId: string }>()
	const [params] = useSearchParams()
	const sel = useBreadcrumbSelection()

	const fromParam = params.get('from')
	const toParam = params.get('to')
	const dateFrom = fromParam
		? new Date(Number(fromParam)).toISOString()
		: moment().subtract(30, 'days').startOf('day').toISOString()
	const dateTo = toParam
		? new Date(Number(toParam)).toISOString()
		: moment().endOf('day').toISOString()

	return {
		accountId,
		assetId,
		body: {
			dateFrom,
			dateTo,
			adPlatforms: sel.adPlatforms.length ? sel.adPlatforms : undefined,
			adAccountIds: sel.adAccountIds.length ? sel.adAccountIds : undefined,
			campaignIds: sel.campaignIds.length ? sel.campaignIds : undefined
		},
		key: [
			accountId,
			assetId,
			dateFrom,
			dateTo,
			sel.adPlatformsCsv,
			sel.adAccountIdsCsv,
			sel.campaignIdsCsv
		],
		ready: !!accountId && !!assetId
	}
}

// Registry metadata (dimension/metric labels, formats, chart/table flags). Static
// per website — cached indefinitely.
export const useAnalyticsMeta = () => {
	const { accountId, assetId, ready } = useAnalyticsScope()
	return useQuery(
		['pa-meta', accountId, assetId],
		() => AnalyticsReport.getDimensions(accountId as string, assetId as string),
		{ enabled: ready, staleTime: Infinity }
	)
}

/**
 * The dimension list the pickers render, server-driven with a synchronous
 * fallback.
 *
 * The static registry supplies ordering and the four categories (product
 * taxonomy, ticket §5.3) plus labels to render before the query resolves. Once
 * `useAnalyticsMeta` lands, the server's label wins and any dimension it
 * reports as unavailable disappears — so a dimension the warehouse can't group
 * by stops being offered without a frontend release. Unknown ids from the
 * server are ignored rather than appended: they'd have no category to sit in.
 */
export const useDimensionCatalog = () => {
	const { data, isLoading } = useAnalyticsMeta()

	const byId = useMemo(() => {
		const map = new Map<string, { label: string; available: boolean }>()
		for (const d of data?.dimensions ?? []) map.set(d.id, { label: d.label, available: d.available })
		return map
	}, [data])

	const categories = useMemo(
		() =>
			DIMENSION_CATEGORIES.map((cat) => ({
				id: cat.id,
				label: cat.label,
				dimensions: cat.dimensionIds
					// Once the server list has loaded, offer ONLY dimensions it actually
					// returned: an id the backend doesn't know (e.g. a stale/uncaptured
					// dimension) 400s every query, so it must not be selectable. Before
					// the query resolves, `data` is undefined and the static set shows so
					// the picker isn't briefly blank.
					.filter((id) => {
						if (!data) return true
						const meta = byId.get(id)
						return !!meta && meta.available !== false
					})
					.map((id) => ({ id, label: byId.get(id)?.label ?? DIMENSIONS[id]?.label ?? id }))
			})).filter((cat) => cat.dimensions.length > 0),
		[byId, data]
	)

	const labelFor = useCallback(
		(id: DimensionId) => byId.get(id)?.label ?? DIMENSIONS[id]?.label ?? id,
		[byId]
	)

	return { categories, labelFor, isLoading }
}

// Section 1 — the 16 scalar metric cards (+ previous-period growth).
export const useMetricCards = () => {
	const { accountId, assetId, body, key, ready } = useAnalyticsScope()
	return useQuery(
		['pa-metrics', ...key],
		() => AnalyticsReport.getMetrics(accountId as string, assetId as string, body),
		{ enabled: ready }
	)
}

// Section 2 — one chart: a metric broken down by a dimension and, for a Stacked
// bar, split by a second one (`dimension2`). The live chart and its in-drawer
// preview call this with the same args, so they can't diverge (same cache entry).
export const useBreakdown = (
	metric: AnalyticsMetricId | undefined,
	dimension: AnalyticsDimensionId | undefined,
	dimension2?: AnalyticsDimensionId,
	topN = CHART_MAX_CATEGORIES
) => {
	const { accountId, assetId, body, key, ready } = useAnalyticsScope()
	// A stack-by equal to the primary is not a stack (the backend rejects it).
	const stackBy = dimension2 && dimension2 !== dimension ? dimension2 : undefined
	return useQuery(
		['pa-breakdown', metric, dimension, stackBy ?? '', topN, ...key],
		() =>
			AnalyticsReport.getBreakdown(accountId as string, assetId as string, {
				...body,
				metric: metric as AnalyticsMetricId,
				dimension: dimension as AnalyticsDimensionId,
				dimension2: stackBy,
				topN
			}),
		{ enabled: ready && !!metric && !!dimension }
	)
}

export interface TableQueryArgs {
	dimension: AnalyticsDimensionId
	parentPath?: ParentFilter[]
	filter?: 'all' | 'bad_rate_gt_10' | 'bad_rate_gt_25'
	sortMetric?: AnalyticsMetricId
	sortDir?: 'asc' | 'desc'
	search?: string
	page?: number
	pageSize?: number
}

// Shared so useTable and the prefetcher build byte-identical cache keys (a drift
// would make a prefetch miss the cache the live query then re-fetches).
const tableQueryKey = (args: TableQueryArgs, scopeKey: (string | undefined)[]) => {
	const parentKey = (args.parentPath ?? [])
		.map((p) => `${p.dimension}:${p.value}`)
		.join('|')
	return [
		'pa-table',
		args.dimension,
		parentKey,
		args.filter,
		args.sortMetric,
		args.sortDir,
		args.search,
		args.page,
		args.pageSize,
		...scopeKey
	]
}

const fetchTable = (
	accountId: string,
	assetId: string,
	body: AnalyticsScopeBody,
	args: TableQueryArgs,
	signal?: AbortSignal
) => AnalyticsReport.getTable(accountId, assetId, { ...body, ...args }, signal)

// Section 3 — segmentation table rows. Top-level rows omit parentPath; drill-down
// child rows pass the parents' (dimension, value) so metrics scope to the parent.
// Lazy: a child query only runs when `enabled` (i.e. the row is expanded).
export const useTable = (args: TableQueryArgs, enabled = true) => {
	const { accountId, assetId, body, key, ready } = useAnalyticsScope()
	return useQuery(
		tableQueryKey(args, key),
		({ signal }) => fetchTable(accountId as string, assetId as string, body, args, signal),
		{ enabled: ready && enabled && !!args.dimension, keepPreviousData: true }
	)
}

// Warm the react-query cache for a table view so switching to it is instant. Used
// to background-prefetch the dimensions the user is browsing in the picker. No-op
// if the scope isn't ready or that exact view is already cached (avoids re-fetching
// a stale-but-present entry, and dedupes repeated hovers).
export const usePrefetchTable = () => {
	const queryClient = useQueryClient()
	const { accountId, assetId, body, key, ready } = useAnalyticsScope()
	return useCallback(
		(args: TableQueryArgs) => {
			if (!ready || !accountId || !assetId || !args.dimension) return
			const qk = tableQueryKey(args, key)
			if (queryClient.getQueryData(qk)) return
			void queryClient.prefetchQuery(qk, ({ signal }) =>
				fetchTable(accountId, assetId, body, args, signal)
			)
		},
		[queryClient, accountId, assetId, ready, JSON.stringify(key)]
	)
}
